// @file Pinia store for additional surface settings loaded after initial page load
import { SurfaceSettingsApi, type SurfaceSettings } from '@@/surface/api/surface_settings'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useSurfaceStartingStateStore } from './surface_starting_state'

export const useSurfaceAdditionalSettingsStore = defineStore('surfaceAdditionalSettings', () => {
  // Surface settings that are loaded after the initial surface load
  const surfaceSettings = ref<SurfaceSettings | null>(null)
  const isSurfaceSettingsLoading = ref(false)
  const surfaceSettingsError = ref<Error | null>(null)
  let surfaceSettingsPromise: Promise<void> | null = null

  const canInviteStudents = computed<boolean>(() => surfaceSettings.value?.canInviteStudents ?? false)
  const canAddClassToWall = computed<boolean>(() => surfaceSettings.value?.canAddClassToWall ?? false)

  async function fetchSurfaceSettings(): Promise<void> {
    // If already loaded, return immediately
    if (surfaceSettings.value != null) return

    // If already loading, return the in-flight promise to avoid duplicate requests
    if (surfaceSettingsPromise != null) return surfaceSettingsPromise

    isSurfaceSettingsLoading.value = true
    surfaceSettingsError.value = null

    surfaceSettingsPromise = (async () => {
      try {
        const startingStateStore = useSurfaceStartingStateStore()
        const wallId = startingStateStore.startingState.wall?.id

        if (!wallId) {
          throw new Error('Wall ID not available')
        }

        const response = await SurfaceSettingsApi.fetch(wallId)
        surfaceSettings.value = response
      } catch (error) {
        surfaceSettingsError.value = error as Error
        // Set default values on error
        surfaceSettings.value = {
          canInviteStudents: false,
          canAddClassToWall: false,
        }
      } finally {
        isSurfaceSettingsLoading.value = false
        surfaceSettingsPromise = null
      }
    })()

    return surfaceSettingsPromise
  }

  return {
    surfaceSettings,
    isSurfaceSettingsLoading,
    surfaceSettingsError,
    canInviteStudents,
    canAddClassToWall,
    fetchSurfaceSettings,
  }
})
